var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { format } from "date-fns";
import { ConsentUIFlow, IdentifiedPatientState } from "healthex-common/consent.dtos";
import { StudyStatus } from "healthex-common/study.dtos";
import { getFullName } from "healthex-common/utils";
import { computed, observable } from "mobx";
import { Disposer } from "../Infra/Disposer";
import { NOT_LOADED } from "../Infra/RemoteData";
import { NamedRoutes } from "./NamedRoutes";
import { PatientConsentFlow, PatientConsentLink } from "./PatientConsentUIManager";
import { SimpleDialogModel } from "./SimpleDialogModel";
export var PatientResearchStudyDetailsBannerState;
(function (PatientResearchStudyDetailsBannerState) {
    PatientResearchStudyDetailsBannerState["CHECKED"] = "CHECKED";
    PatientResearchStudyDetailsBannerState["WARNING"] = "WARNING";
    PatientResearchStudyDetailsBannerState["ERROR"] = "ERROR";
})(PatientResearchStudyDetailsBannerState || (PatientResearchStudyDetailsBannerState = {}));
export var PatientDetailsHealthcareOrgConsentState;
(function (PatientDetailsHealthcareOrgConsentState) {
    PatientDetailsHealthcareOrgConsentState["STUDY_INACTIVE"] = "STUDY_INACTIVE";
    PatientDetailsHealthcareOrgConsentState["STUDY_INACTIVE_PAUSED"] = "STUDY_INACTIVE_PAUSED";
    PatientDetailsHealthcareOrgConsentState["ENROLLMENT"] = "ENROLLMENT";
    PatientDetailsHealthcareOrgConsentState["ALREADY_ENROLLED"] = "ALREADY_ENROLLED";
    PatientDetailsHealthcareOrgConsentState["DECLINED_ENROLLMENT"] = "DECLINED_ENROLLMENT";
    PatientDetailsHealthcareOrgConsentState["REMOVED_ENROLLMENT"] = "REMOVED_ENROLLMENT";
})(PatientDetailsHealthcareOrgConsentState || (PatientDetailsHealthcareOrgConsentState = {}));
export class PatientResearchStudyDetailsViewModelProps {
    constructor(container, modalStudyId, hideStudyDetailsDialog) {
        this.container = container;
        this.modalStudyId = modalStudyId;
        this.hideStudyDetailsDialog = hideStudyDetailsDialog;
    }
}
export class PatientDetailsHealthcareOrgViewModel {
    constructor(container) {
        this.shareDialogModel = new SimpleDialogModel();
        this.disponser = new Disposer();
        this.searchResults = NOT_LOADED;
        this.routingUIManager = container.routingUIManager;
        this.authManager = container.authManager;
        this.mrnSearchService = container.mrnSearchService;
        this.studyManager = container.studyManager;
        this.studyRecruitmentManager = container.studyRecruitmentManager;
        this.patientConsentUIManager = container.patientConsentUIManager;
    }
    run() {
        this.disponser.reaction(() => [this.mrn, this.studyId], async () => {
            await this.searchForPatient();
        }, { fireImmediately: true });
    }
    stop() {
        this.disponser.dispose();
    }
    get study() {
        if (this.searchResults === NOT_LOADED)
            return null;
        return this.searchResults.study;
    }
    get isStudyActive() {
        if (!this.study)
            return false;
        return ![StudyStatus.COMPLETED, StudyStatus.WITHDRAWN].includes(this.study.status) && !this.study.isCompletionDateExpired;
    }
    get isStudyPaused() {
        var _a;
        return !!((_a = this.study) === null || _a === void 0 ? void 0 : _a.paused);
    }
    get continueEnrollmentGuidedConsentExperience() {
        var _a;
        if (this.searchResults === NOT_LOADED)
            return false;
        return !!((_a = this.searchResults.consentFlowProgress.find((consent) => consent.consentUIFlow === ConsentUIFlow.PATIENT_DIRECTED_DATA_EXCHANGE)) === null || _a === void 0 ? void 0 : _a.continueGuidedConsent);
    }
    get patient() {
        if (this.searchResults === NOT_LOADED)
            return null;
        return this.searchResults.patient;
    }
    get identifiedPatient() {
        if (this.searchResults === NOT_LOADED)
            return null;
        return this.searchResults.identifiedPatient;
    }
    get mrn() {
        var _a, _b;
        return (_b = (_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.mrn) !== null && _b !== void 0 ? _b : null;
    }
    get studyId() {
        var _a, _b;
        return (_b = (_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.studyId) !== null && _b !== void 0 ? _b : null;
    }
    get declinedConsent() {
        if (this.identifiedPatient === null)
            return false;
        return [IdentifiedPatientState.DECLINED_PRESCREEN, IdentifiedPatientState.DECLINED_ENROLLMENT].includes(this.identifiedPatient.state);
    }
    get removedConsent() {
        if (this.identifiedPatient === null)
            return false;
        return this.identifiedPatient.state === IdentifiedPatientState.REMOVED;
    }
    get patientConsentState() {
        if (this.identifiedPatient === null)
            return PatientDetailsHealthcareOrgConsentState.ENROLLMENT;
        const identifiedPatientState = this.identifiedPatient.state;
        if (!this.isStudyActive)
            return PatientDetailsHealthcareOrgConsentState.STUDY_INACTIVE;
        if ((this.declinedConsent || this.removedConsent) && this.isStudyPaused) {
            return PatientDetailsHealthcareOrgConsentState.STUDY_INACTIVE_PAUSED;
        }
        if (identifiedPatientState === IdentifiedPatientState.ENROLLED)
            return PatientDetailsHealthcareOrgConsentState.ALREADY_ENROLLED;
        if (this.declinedConsent) {
            return PatientDetailsHealthcareOrgConsentState.DECLINED_ENROLLMENT;
        }
        if (this.removedConsent) {
            return PatientDetailsHealthcareOrgConsentState.REMOVED_ENROLLMENT;
        }
        return PatientDetailsHealthcareOrgConsentState.ENROLLMENT;
    }
    get patientConsentInfo() {
        var _a, _b, _c;
        const isReengage = this.removedConsent || this.declinedConsent;
        let text;
        let bannerText;
        let bannerState;
        let showPrescreeningButton = true;
        let showEnrollmentButton = true;
        let enrollmentInterestedUpdatedText;
        switch (this.patientConsentState) {
            case PatientDetailsHealthcareOrgConsentState.STUDY_INACTIVE:
                bannerText = "This project is no longer adding patients. No further action can be taken.";
                bannerState = PatientResearchStudyDetailsBannerState.ERROR;
                showPrescreeningButton = false;
                showEnrollmentButton = false;
                break;
            case PatientDetailsHealthcareOrgConsentState.STUDY_INACTIVE_PAUSED:
                bannerText = this.declinedConsent
                    ? "This project is currently paused, and this patient has already declined consent for this project. No further action can be taken."
                    : "This project is currently paused, and this patient has been removed by a team member managing the project. No further action can be taken.";
                bannerState = PatientResearchStudyDetailsBannerState.ERROR;
                showPrescreeningButton = false;
                showEnrollmentButton = false;
                break;
            case PatientDetailsHealthcareOrgConsentState.ENROLLMENT:
                text = "This patient is eligible to consent to this project.";
                showPrescreeningButton = false;
                break;
            case PatientDetailsHealthcareOrgConsentState.ALREADY_ENROLLED:
                bannerText = "This patient has already provided consent for this project. No further action is needed.";
                bannerState = PatientResearchStudyDetailsBannerState.CHECKED;
                showEnrollmentButton = false;
                showPrescreeningButton = false;
                break;
            case PatientDetailsHealthcareOrgConsentState.DECLINED_ENROLLMENT:
                bannerText =
                    "This patient has already declined consent for this project. You can choose to re-engage the patient if you still want to proceed with guided consent.";
                bannerState = PatientResearchStudyDetailsBannerState.WARNING;
                showPrescreeningButton = false;
                break;
            case PatientDetailsHealthcareOrgConsentState.REMOVED_ENROLLMENT:
                bannerText =
                    "This patient has been excluded by a team member managing the project. You can choose to re-engage the patient if you still want to proceed with guided consent.";
                bannerState = PatientResearchStudyDetailsBannerState.WARNING;
                showPrescreeningButton = false;
                break;
        }
        if (this.isStudyPaused && !isReengage) {
            text = undefined;
            bannerText =
                "This study is currently paused by a team member managing the project. You can continue if you still want to proceed with guided consent.";
            bannerState = PatientResearchStudyDetailsBannerState.WARNING;
        }
        if (showEnrollmentButton &&
            this.patient &&
            !((_a = this.identifiedPatient) === null || _a === void 0 ? void 0 : _a.contacted) &&
            ((_b = this.identifiedPatient) === null || _b === void 0 ? void 0 : _b.interested) &&
            ((_c = this.identifiedPatient) === null || _c === void 0 ? void 0 : _c.interestedUpdated)) {
            const date = format(new Date(this.identifiedPatient.interestedUpdated), "LLL dd, yyyy");
            enrollmentInterestedUpdatedText = `${getFullName(this.patient)} reviewed this request on ${date} and indicated that they are interested in learning more. Someone from the study team will reach out to provide more details.`;
        }
        return {
            text,
            bannerText,
            bannerState,
            enrollmentButtonProps: showEnrollmentButton
                ? {
                    children: isReengage
                        ? "Re-engage and start guided enrollment consent"
                        : this.continueEnrollmentGuidedConsentExperience
                            ? "Continue guided enrollment consent"
                            : "Start guided consent",
                    onClick: async () => {
                        if (!this.studyId || !this.mrn)
                            return;
                        if (this.removedConsent)
                            await this.addBackToCohort();
                        if (this.declinedConsent)
                            await this.sendEnrollmentRequest();
                        this.patientConsentUIManager.startConsentFlow(this.studyId, PatientConsentFlow.ENROLLMENT, this.mrn, !this.identifiedPatient ? PatientConsentLink.LINK : null);
                    },
                }
                : undefined,
            enrollmentTitle: "Guided Consent",
            enrollmentText: "Walk patients through a consent request",
            enrollmentInterestedUpdatedText,
        };
    }
    get showConsentButtonsSection() {
        return !!this.patientConsentInfo.enrollmentButtonProps;
    }
    onBack() {
        if (!this.mrn)
            return;
        this.routingUIManager.navigateTo(NamedRoutes.PATIENTS_SEARCH, { mrn: this.mrn });
    }
    showShareDialog() {
        this.shareDialogModel.show();
    }
    async searchForPatient() {
        if (!this.mrn || !this.studyId)
            return;
        const searchResults = await this.mrnSearchService.searchByMRNAndGetIdentifiedPatientDetails(this.authManager.orgId, {
            mrn: this.mrn,
            studyId: this.studyId,
        });
        this.searchResults = searchResults;
    }
    async sendEnrollmentRequest() {
        if (this.study === null || this.identifiedPatient === null)
            return;
        try {
            await this.studyManager.requestPatientStudyConsentForEnrollment(this.study, [this.identifiedPatient]);
        }
        catch (e) {
            console.error("Sending enrollment request failed", e);
        }
    }
    async addBackToCohort() {
        if (this.study === null || this.identifiedPatient === null)
            return;
        await this.studyRecruitmentManager.updateIdentifiedPatientForStudy(this.study.id, this.study.addedByOrganization.id, this.identifiedPatient.id, {
            removed: false,
        });
    }
}
__decorate([
    observable
], PatientDetailsHealthcareOrgViewModel.prototype, "searchResults", void 0);
__decorate([
    computed
], PatientDetailsHealthcareOrgViewModel.prototype, "study", null);
__decorate([
    computed
], PatientDetailsHealthcareOrgViewModel.prototype, "isStudyActive", null);
__decorate([
    computed
], PatientDetailsHealthcareOrgViewModel.prototype, "isStudyPaused", null);
__decorate([
    computed
], PatientDetailsHealthcareOrgViewModel.prototype, "continueEnrollmentGuidedConsentExperience", null);
__decorate([
    computed
], PatientDetailsHealthcareOrgViewModel.prototype, "patient", null);
__decorate([
    computed
], PatientDetailsHealthcareOrgViewModel.prototype, "identifiedPatient", null);
__decorate([
    computed
], PatientDetailsHealthcareOrgViewModel.prototype, "mrn", null);
__decorate([
    computed
], PatientDetailsHealthcareOrgViewModel.prototype, "studyId", null);
__decorate([
    computed
], PatientDetailsHealthcareOrgViewModel.prototype, "declinedConsent", null);
__decorate([
    computed
], PatientDetailsHealthcareOrgViewModel.prototype, "removedConsent", null);
__decorate([
    computed
], PatientDetailsHealthcareOrgViewModel.prototype, "patientConsentState", null);
__decorate([
    computed
], PatientDetailsHealthcareOrgViewModel.prototype, "patientConsentInfo", null);
__decorate([
    computed
], PatientDetailsHealthcareOrgViewModel.prototype, "showConsentButtonsSection", null);
